import { DETAIL_STATUS } from '../../common/DetailStatusChip';
import { estimateInvoices, estimateWorkOrderToSync, firstLinked, getEstimateDetailUrl, getWorkOrderSyncFromEstimateUrl } from '../../common/LinkedDocumentLink';
import SyncUpdateIcon from '../../utility/Svg/SyncUpdateIcon';
import { useTheme } from '@emotion/react';
import { Add, Close, Delete, Edit, MoreVert, Receipt, Visibility } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteFinalizedEstimate, estimateData as estimateDetailApi, estimateDetails, sendEstimate } from '../../redux/Estimate/action';
import useEstimateStore from '../../store/useEstimateStore';
import ManageColumnsMenu from '../modal/Managecolumnsmenu.jsx';
import SelectVehicleModal from '../modal/SelectVehicleModal';
import './Estimates.css';

import dayjs from 'dayjs';
import { ESTIMATE_REQUEST_COLOR } from '../../App';
import CommonDialog from '../../common/CommonDialog';
import queryClient from '../../config/queryClient';
import { customerDetail } from '../../redux/Customer/action';
import { requestEstimateDetails } from '../../redux/Dashboard/action';
import { estimateQueryKeys } from '../../services/business/estimates/estimates.hooks';
import { CHIP_COLOR, ESTIMATE_STATUS, isIssuedEstimate, normalizeWorkOrderStatus, WORK_ORDER_STATUS } from '../../utility/Constant';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import CreateWorkOrderSvg from '../../utility/Svg/CreateWorkOrderSvg';
import ResendSvg from '../../utility/Svg/ResendSvg';
import EstimateRequestModal from '../modal/EstimateRequestModal';

/**
 * Tabs in order: `segment` is the route (/estimates/<segment>), `status` the list API filter.
 * Work Order and Invoiced list the estimates converted to a work order / invoice.
 */
const ESTIMATE_TABS = [
    { label: 'ALL', segment: 'all', status: 'all' },
    { label: 'FINALIZED', segment: 'finalized', status: 'finalized' },
    { label: 'Sent', segment: 'sents', status: 'sent' },
    { label: 'Work Order', segment: 'work-orders', status: ESTIMATE_STATUS.WORK_ORDER },
    { label: 'Invoiced', segment: 'invoiced', status: ESTIMATE_STATUS.INVOICED },
    { label: 'drafts', segment: 'drafts', status: 'draft' },
    { label: 'estimate requests', segment: 'requests', status: 'requests' },
];

/** Status chip on a list row — label and colours. */
const getEstimateRowChip = (row) => {
    switch (true) {
        case row?.status === 'decline':
            return { label: 'Declined', bg: ESTIMATE_REQUEST_COLOR, color: CHIP_COLOR.DEFAULT_COLOR };
        case row?.status === 'accept':
            return { label: 'Accepted', bg: ESTIMATE_REQUEST_COLOR, color: CHIP_COLOR.DEFAULT_COLOR };
        case row?.status === ESTIMATE_STATUS.DRAFT:
            return { label: 'Draft', bg: CHIP_COLOR.DRAFT, color: CHIP_COLOR.DEFAULT_COLOR };
        case row?.status === ESTIMATE_STATUS.INVOICED:
            return { label: DETAIL_STATUS.INVOICED.label, bg: DETAIL_STATUS.INVOICED.bg, color: DETAIL_STATUS.INVOICED.color };
        case row?.status === ESTIMATE_STATUS.WORK_ORDER:
            return { label: DETAIL_STATUS.WORK_ORDER.label, bg: DETAIL_STATUS.WORK_ORDER.bg, color: DETAIL_STATUS.WORK_ORDER.color };
        case row?.status === ESTIMATE_STATUS.SENT && row?.isEmailSent:
            return { label: 'Sent', bg: CHIP_COLOR.SENT, color: CHIP_COLOR.DEFAULT_COLOR };
        default:
            return { label: 'Finalized', bg: CHIP_COLOR.FINALIZED, color: CHIP_COLOR.DEFAULT_COLOR };
    }
};

const EstimateRowStatusChip = ({ row }) => {
    const { label, bg, color } = getEstimateRowChip(row);
    return <Chip label={label} style={{ backgroundColor: bg, color, fontWeight: '500', lineHeight: '18px', letterSpacing: '0.16px' }} />;
};

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && (
                <Box sx={{ pt: 4 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function formatEstimateVehicleDisplay(vehicle) {
    if (!vehicle) return '—';
    const year = vehicle.year != null && vehicle.year !== '' ? String(vehicle.year) : '';
    const make = vehicle.vehicleMake?.name ?? '';
    const model = vehicle.vehicleModel?.name ?? '';
    const parts = [year, make, model].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
}

function formatEstimateLicensePlateDisplay(vehicle) {
    if (!vehicle?.licenseNumber) return '—';
    return vehicle?.provinceCode ? `${vehicle.provinceCode} ${vehicle.licenseNumber}` : String(vehicle.licenseNumber);
}

/** 6 data columns share remaining width; last column is fixed 48px (actions). */
const ESTIMATE_LIST_TABLE_SX = { tableLayout: 'auto', width: '100%' };

const ESTIMATE_LIST_ACTION_COL = { width: 48, minWidth: 48, maxWidth: 48 };
const ESTIMATE_LIST_COL = {}; // no width — browser decides
const ESTIMATE_LIST_BODY_CELL = { whiteSpace: 'nowrap' }; // prevents awkward wrapping

const Estimates = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        search,
        setSearch,
        tabValue: value,
        setTabValue: setValue,
        type,
        setType,
        dateData,
        setDateData,
        selectDate,
        setSelectDate,
        yearData,
        setYearData,
        makeData,
        setMakeData,
        modelData,
        setModelData,
        licenseNumber,
        setLicenseNumber,
        fleetUnitNumber,
        setFleetUnitNumber,
        provinceId,
        setProvinceId,
        vinFilter,
        setVinFilter,
        sortStatus,
        setSortStatus,
        sortByName,
        setSortByName,
        sortTypeTotal,
        setSortTypeTotal: setsortTypeTotal,
        showEstimateLicensePlateColumn,
        setShowEstimateLicensePlateColumn,
        showEstimateUnitColumn,
        setShowEstimateUnitColumn,
        resetEstimateTableColumns,
        estimateCheckboxes: state,
        setEstimateCheckboxes: setState,
        resetEstimateStore,
    } = useEstimateStore();

    const location = useLocation();

    useEffect(() => {
        const prevPath = sessionStorage.getItem('prevPath');
        const isInternal = prevPath && prevPath.startsWith('/estimates');
        if (!isInternal) {
            resetEstimateStore();
        }
        sessionStorage.setItem('prevPath', location.pathname);
    }, [location.pathname]);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // State variables
    const [estimateData, setEstimateData] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [open2, setOpen2] = useState(false);
    const [estimateId, setEstimateId] = useState('');
    const [estimateDetail, setEstimateDetail] = useState('');
    const [sortName, setSortName] = useState('ASC');
    const [sortCategory, setSortCategory] = useState('ASC');
    const [status, setStatus] = useState('all');
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const open1 = Boolean(anchorEl);
    const estimate = useSelector((state) => state.estimate.estimateData);

    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const [anchorElDropdown, setAnchorElDropdown] = useState(null);
    const [estimateColumnAnchorEl, setEstimateColumnAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState({});
    // { message, onContinue } while an "Invoice Already Exists" warning is showing.

    const openDropdown = Boolean(anchorElDropdown);
    const openEstimateColumnSettings = Boolean(estimateColumnAnchorEl);

    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        event.preventDefault();

        setAnchorElDropdown(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorElDropdown(null);
        // setSelectedRow(null);
    };

    const handleEstimateColumnSettingsOpen = (event) => {
        setEstimateColumnAnchorEl(event.currentTarget);
    };

    const handleEstimateColumnSettingsClose = () => {
        setEstimateColumnAnchorEl(null);
    };

    const fetchCustomerDetail = async (data) => {
        setShowLoader(true);

        await dispatch(customerDetail(data?.customer?._id));
        setShowLoader(false);
    };

    const handleSend = async () => {
        try {
            setShowLoader(true);
            const data = await dispatch(sendEstimate(selectedRow?._id));
            if (data) {
                setOpenSuccessModal(true);
                setSuccessMessage('Estimate was successfully emailed to customer');
                handleClose1();
            }
        } catch (error) {
            console.log('🚀 ~ handleSend ~ error:', error);
        } finally {
            setShowLoader(false);
        }
    };
    // Only invoices made directly from this estimate — an invoice that came through its work order
    const selectedRowOwnInvoices = estimateInvoices(selectedRow);
    /*
     * Which conversion options this estimate offers (spec §6.4).
     * Scenario 1 — no work order and no invoice: both Create Work Order and Convert to Invoice.
     * Later scenarios tighten these two flags; nothing else decides the options.
     */
    /** The estimate's own invoice — the one it can update (scenario 4). */
    const ownInvoice = selectedRowOwnInvoices[0] || null;
    const linkedWorkOrder = firstLinked(selectedRow?.linkedWorkOrderId);
    const hasLinkedWorkOrder = Boolean(linkedWorkOrder?._id);
    /** Scenario 7 — the invoice made from the work order: the estimate links to it, view-only. */
    const workOrderInvoice = firstLinked(linkedWorkOrder?.linkedInvoiceId);
    const linkedInvoice = ownInvoice || workOrderInvoice || null;
    const hasLinkedInvoice = Boolean(linkedInvoice?._id);
    const isLinkedInvoiceOutOfDate = Boolean(ownInvoice) && ownInvoice?.isSyncedWithEstimate === false;
    // Scenario 5 — with a work order in between, the invoice is created from the work order, so the
    // estimate does not offer Convert to Invoice.
    const canConvertToInvoice = !hasLinkedInvoice && !hasLinkedWorkOrder;
    // Scenarios 2 and 3 — any linked invoice, draft or not, blocks a new work order. Blocked hides the
    // option entirely; with no work order there is nothing to view either.
    const canCreateWorkOrder = !hasLinkedWorkOrder && !hasLinkedInvoice;

    const getLinkedInvoiceUrl = () => {
        const row = linkedInvoice;
        if (!row?._id) return '#';
        let status;

        if (row?.status === 'draft') {
            status = 'draft';
        } else if (row?.status === 'sent') {
            status = row.isEmailSent ? 'sent' : 'finalized';
        } else {
            status = row.status;
        }

        return `/invoice/${status}/${row?._id}`;
    };

    /** Work order detail url by status — mirrors getLinkedInvoiceUrl above. */
    const getLinkedWorkOrderUrl = () => {
        const workOrders = selectedRow?.linkedWorkOrderId;
        if (!workOrders || workOrders.length === 0) return '#';

        const row = workOrders[0];
        return `/work-orders/${normalizeWorkOrderStatus(row?.status) || WORK_ORDER_STATUS.OPEN}/${row?._id}`;
    };

    // Status mapping for tabs
    const statusMap = useMemo(() => Object.fromEntries(ESTIMATE_TABS.map((tab, i) => [i, tab.status])), []);

    const routeMap = useMemo(() => Object.fromEntries(ESTIMATE_TABS.map((tab, i) => [tab.segment, i])), []);

    // Memoized params object
    const params = useMemo(
        () => ({
            page: page + 1,
            limit: rowsPerPage,
            status: status,
            sortType: sortStatus,
            // sortType: sortTypeTotal,
            sortBy: sortByName,
            text: search,
            type,
            year: yearData,
            makeId: makeData,
            modelId: modelData,
            date: dateData,
            licenseNumber,
            fleetUnitNumber,
            provinceId,
            vin: vinFilter,
        }),
        [
            page,
            rowsPerPage,
            status,
            sortStatus,
            sortByName,
            search,
            type,
            yearData,
            makeData,
            modelData,
            dateData,
            licenseNumber,
            fleetUnitNumber,
            provinceId,
            vinFilter,
        ]
    );

    // Debounce function
    const debounce = useCallback((func, wait) => {
        let timeout;
        return function (...args) {
            const context = this;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                timeout = null;
                func.apply(context, args);
            }, wait);
        };
    }, []);

    // Estimate details API call
    const estimateDetailsData = useCallback(
        async (requestParams) => {
            setShowLoader(true);
            try {
                const data = await dispatch(estimateDetails(requestParams));
                if (data) {
                    setEstimateData(data);
                }
            } catch (error) {
                console.error('Error fetching estimate details:', error);
            } finally {
                setShowLoader(false);
            }
        },
        [dispatch]
    );

    // Initialize component based on URL
    useEffect(() => {
        const nameArray = window.location.pathname.split('/');
        const routeName = nameArray[nameArray.length - 1];

        // Reset pagination
        // setPage(0);
        // setRowsPerPage(10);

        // Set tab and status based on route
        const tabIndex = routeMap[routeName] !== undefined ? routeMap[routeName] : 0;
        const newStatus = statusMap[tabIndex];

        setValue(tabIndex);
        setStatus(newStatus);

        // Fetch data with updated params
        const updatedParams = {
            ...params,
            page: page + 1,
            limit: rowsPerPage,
            status: newStatus,
        };

        estimateDetailsData(updatedParams);
    }, [window.location.pathname, estimateDetailsData]);

    // Tab change handler
    const handleChange = useCallback(
        (event, newValue) => {
            setValue(newValue);
            setPage(0);

            const newStatus = statusMap[newValue];
            setStatus(newStatus);

            // Navigate to appropriate route
            navigate(`/estimates/${ESTIMATE_TABS[newValue].segment}`);

            // Fetch data with new status
            const updatedParams = {
                ...params,
                page: 1,
                limit: 10,
                status: newStatus,
            };

            estimateDetailsData(updatedParams);
        },
        [navigate, params, statusMap, estimateDetailsData]
    );

    // Search handler with debounce
    const onChange = useCallback(
        async (searchText, tabValue, model, year, make) => {
            const updatedParams = {
                ...params,
                text: searchText,
                year: year,
                makeId: make,
                modelId: model,
                page: 1,
            };

            setSearch(searchText);
            estimateDetailsData(updatedParams);
        },
        [params, estimateDetailsData]
    );

    const debounceOnChange = React.useCallback(debounce(onChange, 800), []);

    // Pagination handlers
    const handleChangePage = useCallback(
        (event, newPage) => {
            setPage(newPage);
            const updatedParams = {
                ...params,
                page: newPage + 1,
            };
            estimateDetailsData(updatedParams);
        },
        [params, estimateDetailsData]
    );

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);

            const updatedParams = {
                ...params,
                limit: newRowsPerPage,
                page: 1,
            };
            estimateDetailsData(updatedParams);
        },
        [params, estimateDetailsData]
    );

    // Modal handlers
    const handleClose2 = useCallback(() => {
        setOpen2(false);
        const updatedParams = {
            ...params,
            status: 'requests',
        };
        estimateDetailsData(updatedParams);
    }, [params, estimateDetailsData]);

    const handleOpen2 = useCallback(() => {
        setOpen2(true);
    }, []);

    const handleOpen = useCallback(() => setOpen(true), []);

    const handleVehicleCancel = useCallback(() => {
        // Cancel should restore applied values (store unchanged)
        setOpen(false);
    }, []);

    const handleVehicleApply = useCallback(
        (next) => {
            const nextYear = next?.yearData ?? '';
            const nextMake = next?.makeData ?? '';
            const nextModel = next?.modelData ?? '';
            const nextLicense = next?.licenseNumber ?? '';
            const nextUnit = next?.fleetUnitNumber ?? '';
            const nextProvince = next?.provinceId ?? '';
            const nextVin = next?.vinFilter ?? '';

            setYearData(nextYear);
            setMakeData(nextMake);
            setModelData(nextModel);
            setLicenseNumber(nextLicense);
            setFleetUnitNumber(nextUnit);
            setProvinceId(nextProvince);
            setVinFilter(nextVin);

            setPage(0);
            estimateDetailsData({
                ...params,
                page: 1,
                year: nextYear,
                makeId: nextMake,
                modelId: nextModel,
                licenseNumber: nextLicense,
                fleetUnitNumber: nextUnit,
                provinceId: nextProvince,
                vin: nextVin,
            });

            setOpen(false);
        },
        [estimateDetailsData, params, setFleetUnitNumber, setLicenseNumber, setMakeData, setModelData, setProvinceId, setVinFilter, setYearData, setPage]
    );

    const handleCancel = useCallback(() => {
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setPage(0);

        const updatedParams = {
            ...params,
            page: 1,
            year: '',
            makeId: '',
            modelId: '',
            licenseNumber: '',
            fleetUnitNumber: '',
            provinceId: '',
            vin: '',
        };
        estimateDetailsData(updatedParams);
        setOpen(false);
    }, [params, estimateDetailsData, setYearData, setMakeData, setModelData, setLicenseNumber, setFleetUnitNumber, setProvinceId, setVinFilter, setPage]);

    const handleClick1 = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose1 = useCallback(() => {
        setAnchorEl(null);
    }, []);

    // Checkbox change handler
    const handleCheckChange = useCallback(
        (event) => {
            const { name, checked } = event.target;
            let newType = '';

            if (name === 'EST') {
                if (checked) {
                    newType = state['ESTR'] ? '' : 'EST';
                } else {
                    newType = state['ESTR'] ? 'ESTR' : '';
                }
            } else if (name === 'ESTR') {
                if (checked) {
                    newType = state['EST'] ? '' : 'ESTR';
                } else {
                    newType = state['EST'] ? 'EST' : '';
                }
            }

            setType(newType);
            setState((prev) => ({ ...prev, [name]: checked }));
            handleClose1();

            const updatedParams = {
                ...params,
                type: newType,
            };
            estimateDetailsData(updatedParams);
        },
        [state, params, estimateDetailsData, handleClose1]
    );

    // Sort handler
    const handleSortByName = useCallback(
        (sortType, sortBy) => {
            setSortStatus(sortType);
            setSortByName(sortBy);
            setsortTypeTotal(sortType);

            const updatedParams = {
                ...params,
                sortBy: sortBy,
                sortType: sortType,
            };

            estimateDetailsData(updatedParams);

            if (sortBy === 'name') {
                setSortName(sortType === 'DSC' ? 'ASC' : 'DSC');
            } else if (sortBy === 'Total') {
                setsortTypeTotal(sortType === 'DSC' ? 'ASC' : 'DSC');
            } else {
                setSortCategory(sortType === 'DSC' ? 'ASC' : 'DSC');
            }
        },
        [params, estimateDetailsData]
    );

    // Date change handler
    const handleDateChange = useCallback(
        (date) => {
            // Clear when null
            if (date == null) {
                setSelectDate(null);
                setDateData('');
                const updatedParams = { ...params, date: '' };
                estimateDetailsData(updatedParams);
                return;
            }

            // Guard: only proceed if the date is fully valid
            // This prevents mid-typing calls like when user types "202" of "2026"
            const parsed = dayjs(date);
            if (!parsed.isValid() || parsed.year() < 1900 || parsed.year() > 2100) {
                return; // Silently ignore incomplete/invalid dates
            }

            const utcDate = parsed.format('YYYY-MM-DD');
            setSelectDate(parsed);
            setDateData(utcDate);

            const updatedParams = { ...params, date: utcDate };
            estimateDetailsData(updatedParams);
        },
        [params, estimateDetailsData]
    );

    // Estimate view
    const estimateView = useCallback(async () => {
        if (estimateId) {
            setShowLoader(true);
            try {
                const data = await dispatch(requestEstimateDetails(estimateId));
                if (data) {
                    setEstimateDetail(data);
                    setEstimateId('');
                }
            } catch (error) {
                console.error('Error fetching estimate view:', error);
            } finally {
                setShowLoader(false);
            }
        }
    }, [estimateId, dispatch]);

    useEffect(() => {
        if (estimateId) {
            estimateView();
        }
    }, [estimateId, estimateView]);

    const index = ESTIMATE_TABS.map((_, i) => i);

    const estimateVehicleFieldCount = [
        licenseNumber?.trim?.(),
        fleetUnitNumber?.trim?.(),
        vinFilter?.trim?.(),
        yearData,
        makeData,
        modelData,
        provinceId,
    ].filter(Boolean).length;

    const estimateHasClearableFilters =
        Boolean(dateData) ||
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId) ||
        Boolean(search?.trim()) ||
        Boolean(type);

    const estimateClearFilterGroups = [
        Boolean(dateData),
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId),
        Boolean(search?.trim()),
        Boolean(type),
    ].filter(Boolean).length;
    const estimateClearFilterLabel = estimateClearFilterGroups > 1 ? 'Clear Filters' : 'Clear Filter';

    const handleClearEstimatesFilters = () => {
        setSelectDate(null);
        setDateData('');
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setSearch('');
        setType('');
        setState({ EST: false, ESTR: false });

        // Also hard-clear the DatePicker's input field so no stale text remains
        const dateInput = document.querySelector('#estimate-date-input');
        if (dateInput) {
            dateInput.value = '';
        }

        setPage(0);
        estimateDetailsData({
            page: 1,
            limit: rowsPerPage,
            status: status,
            sortType: sortStatus,
            sortBy: sortByName,
            text: '',
            type: '',
            year: '',
            makeId: '',
            modelId: '',
            date: '',
            licenseNumber: '',
            fleetUnitNumber: '',
            provinceId: '',
            vin: '',
        });
    };

    return (
        <>
            <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
                <Grid container rowGap={2} paddingBottom={'24px'}>
                    <Grid container item justifyContent={'space-between'}>
                        <Grid item lg={9}>
                            <Typography variant="h4" sx={{ fontWeight: 400 }}>
                                Estimates{' '}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                color="secondary"
                                variant="contained"
                                startIcon={<Add />}
                                size="large"
                                onClick={() => {
                                    navigate('/estimates/new');
                                }}
                            >
                                NEW ESTIMATE
                            </Button>
                        </Grid>
                    </Grid>
                    <>
                        <Grid container item margin={'12px 0'} alignItems={'center'} justifyContent={'space-between'}>
                            <Grid item container alignItems={'center'}>
                                <Box sx={{ width: '100%' }}>
                                    <Box>
                                        {/* Scrollable with arrows so all seven tabs fit on smaller screens. */}
                                        <Tabs
                                            value={value}
                                            onChange={handleChange}
                                            aria-label="estimate tabs"
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            allowScrollButtonsMobile
                                        >
                                            {ESTIMATE_TABS.map((tab, i) => (
                                                <Tab key={tab.segment} label={tab.label} {...a11yProps(i)} style={{ fontWeight: 500 }} />
                                            ))}
                                        </Tabs>
                                    </Box>

                                    {index.map((item) => (
                                        <CustomTabPanel key={item} value={value} index={item} padding={'24px'}>
                                            <Grid container item>
                                                <Grid container marginBottom={'32px'} alignItems={'center'} justifyContent={'space-between'}>
                                                    {/* Select Date, Vehicle, and Type */}
                                                    <Grid container xs={12} sm={6} md={8} justifyContent={'flex-start'} alignItems={'center'}>
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                <DatePicker
                                                                    label="Select date"
                                                                    name="proposeDate"
                                                                    value={selectDate}
                                                                    onChange={handleDateChange}
                                                                    sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            sx: {
                                                                                width: 240,
                                                                                minWidth: 240,
                                                                                maxWidth: 240,
                                                                                '& .MuiInputBase-root': { width: '100%' },
                                                                            },
                                                                            inputProps: {
                                                                                id: 'estimate-date-input',
                                                                            },
                                                                        },
                                                                        field: { clearable: true },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>

                                                        {/* Filter by vehicle */}
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <Button
                                                                variant={estimateVehicleFieldCount > 0 ? 'contained' : 'text'}
                                                                color="primary"
                                                                onClick={handleOpen}
                                                                sx={{
                                                                    color: estimateVehicleFieldCount > 0 ? 'white!important' : 'black!important',
                                                                    fontWeight: 500,
                                                                    lineHeight: '24px',
                                                                    padding: '6px 12px!important',
                                                                    height: '40px',
                                                                    // height: '56px!important',
                                                                }}
                                                                startIcon={
                                                                    estimateVehicleFieldCount > 0 && (
                                                                        <Avatar
                                                                            sx={{
                                                                                bgcolor: 'white',
                                                                                color: 'primary.main',
                                                                                fontSize: '14px!important',
                                                                                fontWeight: '500!important',
                                                                                letterSpacing: '0.4px',
                                                                                lineHeight: '24px',
                                                                                width: 24,
                                                                                height: 24,
                                                                            }}
                                                                        >
                                                                            {estimateVehicleFieldCount}
                                                                        </Avatar>
                                                                    )
                                                                }
                                                                endIcon={<ExpandMoreIcon />}
                                                            >
                                                                FILTER BY VEHICLE
                                                            </Button>
                                                            <SelectVehicleModal
                                                                open={open}
                                                                onCancel={handleVehicleCancel}
                                                                onApply={handleVehicleApply}
                                                                yearData={yearData}
                                                                makeData={makeData}
                                                                modelData={modelData}
                                                                licenseNumber={licenseNumber}
                                                                fleetUnitNumber={fleetUnitNumber}
                                                                provinceId={provinceId}
                                                                vinFilter={vinFilter}
                                                            />
                                                        </Grid>

                                                        {/* Select Type - only show on ALL tab */}
                                                        {value === 0 && (
                                                            <Grid item xs="auto" padding={'6px'}>
                                                                <Button
                                                                    variant="text"
                                                                    id="demo-positioned-button"
                                                                    aria-controls={open1 ? 'demo-positioned-menu' : undefined}
                                                                    aria-haspopup="true"
                                                                    aria-expanded={open1 ? 'true' : undefined}
                                                                    onClick={handleClick1}
                                                                    style={{
                                                                        color: 'black',
                                                                        height: '40px',
                                                                        fontWeight: 550,
                                                                        // height: '56px!important'
                                                                    }}
                                                                    endIcon={<ExpandMoreIcon />}
                                                                >
                                                                    SELECT TYPE
                                                                </Button>
                                                                <Menu
                                                                    style={{ width: '300px' }}
                                                                    id="demo-positioned-menu"
                                                                    aria-labelledby="demo-positioned-button"
                                                                    anchorEl={anchorEl}
                                                                    open={open1}
                                                                    onClose={handleClose1}
                                                                    anchorOrigin={{
                                                                        vertical: 'top',
                                                                        horizontal: 'left',
                                                                    }}
                                                                    transformOrigin={{
                                                                        vertical: 'top',
                                                                        horizontal: 'left',
                                                                    }}
                                                                >
                                                                    <MenuItem>
                                                                        <FormControlLabel
                                                                            control={<Checkbox checked={state.EST} onChange={handleCheckChange} name="EST" />}
                                                                            label="Estimate (EST)"
                                                                        />
                                                                    </MenuItem>
                                                                    <MenuItem>
                                                                        <FormControlLabel
                                                                            control={<Checkbox checked={state.ESTR} onChange={handleCheckChange} name="ESTR" />}
                                                                            label="Estimate Request (ESTR)"
                                                                        />
                                                                    </MenuItem>
                                                                </Menu>
                                                            </Grid>
                                                        )}

                                                        {estimateHasClearableFilters && (
                                                            <Grid item xs="auto" padding={'6px'}>
                                                                <Button
                                                                    variant="contained"
                                                                    color="secondary"
                                                                    startIcon={<Close />}
                                                                    onClick={handleClearEstimatesFilters}
                                                                    sx={{
                                                                        background: '#0000001A',
                                                                        color: '#000000',
                                                                        '&:hover': {
                                                                            background: '#0000001A',
                                                                            boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                            // height: '56px!important',
                                                                        },
                                                                        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                        fontWeight: 500,
                                                                        lineHeight: '24px',
                                                                    }}
                                                                >
                                                                    {estimateClearFilterLabel}
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                    {/* Search Bar */}
                                                    <Grid container xs={12} sm={4} md={4} justifyContent={'flex-end'} padding={'6px'}>
                                                        <FormControl fullWidth>
                                                            <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                                                            <OutlinedInput
                                                                id="outlined-adornment-search"
                                                                placeholder="Customer name, service, etc..."
                                                                startAdornment={
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon />
                                                                    </InputAdornment>
                                                                }
                                                                value={search}
                                                                label="Search"
                                                                onChange={(e) => {
                                                                    setSearch(e.target.value);
                                                                    debounceOnChange(e.target.value, value, modelData, yearData, makeData);
                                                                }}
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                                <Grid container item padding={'20px'} boxShadow={2} borderRadius={'8px'} backgroundColor="white">
                                                    <Grid item xl={12} md={12} sm={12} xs={12}>
                                                        <TableContainer sx={{ maxHeight: 600, width: '100%' }}>
                                                            <Table
                                                                stickyHeader
                                                                aria-label="caption table"
                                                                padding="16px"
                                                                sx={ESTIMATE_LIST_TABLE_SX}
                                                                style={{ boxShadow: 0 }}
                                                            >
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell align="left" sx={ESTIMATE_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={true}
                                                                                direction={sortCategory === 'DSC' ? 'desc' : 'asc'}
                                                                                sx={{ fontWeight: 500 }}
                                                                                onClick={() => {
                                                                                    setSortByName('createdAt');
                                                                                    handleSortByName(sortCategory === 'DSC' ? 'DSC' : 'ASC', 'createdAt');
                                                                                }}
                                                                            >
                                                                                Estimate Number
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={ESTIMATE_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={true}
                                                                                direction={sortCategory === 'DSC' ? 'desc' : 'asc'}
                                                                                sx={{ fontWeight: 500 }}
                                                                                onClick={() => {
                                                                                    setSortByName('createdAt');
                                                                                    handleSortByName(sortCategory === 'DSC' ? 'DSC' : 'ASC', 'createdAt');
                                                                                }}
                                                                            >
                                                                                Date
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={{ ...ESTIMATE_LIST_COL }}>
                                                                            <TableSortLabel
                                                                                onClick={() => {
                                                                                    setSortByName('name');
                                                                                    handleSortByName(sortName === 'DSC' ? 'DSC' : 'ASC', 'name');
                                                                                }}
                                                                                active={true}
                                                                                direction={sortName === 'DSC' ? 'desc' : 'asc'}
                                                                                sx={{ fontWeight: 500 }}
                                                                            >
                                                                                Customer Name
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={{ ...ESTIMATE_LIST_COL, fontWeight: 500, minWidth: 160 }}>
                                                                            Vehicle
                                                                        </TableCell>
                                                                        {showEstimateLicensePlateColumn && (
                                                                            <TableCell align="left" sx={{ ...ESTIMATE_LIST_COL, fontWeight: 500 }}>
                                                                                Licence Plate
                                                                            </TableCell>
                                                                        )}
                                                                        {showEstimateUnitColumn && (
                                                                            <TableCell align="left" sx={{ ...ESTIMATE_LIST_COL, fontWeight: 500 }}>
                                                                                Unit #
                                                                            </TableCell>
                                                                        )}
                                                                        <TableCell align="left" sx={ESTIMATE_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={true}
                                                                                direction={sortTypeTotal == 'DSC' ? 'desc' : 'asc'}
                                                                                onClick={() => {
                                                                                    setsortTypeTotal(sortTypeTotal === 'DSC' ? 'ASC' : 'DSC');
                                                                                    handleSortByName(sortTypeTotal == 'DSC' ? 'DSC' : 'ASC', 'Total');
                                                                                }}
                                                                                sx={{ fontWeight: 500 }}
                                                                            >
                                                                                Total
                                                                            </TableSortLabel>
                                                                        </TableCell>

                                                                        <TableCell align="left" sx={{ ...ESTIMATE_LIST_COL, fontWeight: 500 }}>
                                                                            Status
                                                                        </TableCell>

                                                                        <TableCell
                                                                            align="left"
                                                                            sx={{
                                                                                ...ESTIMATE_LIST_COL,
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={handleEstimateColumnSettingsOpen}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    width: 32,
                                                                                    height: 32,
                                                                                    justifyContent: 'center',
                                                                                    alignItems: 'center',
                                                                                    gap: '12px',
                                                                                    flexShrink: 0,
                                                                                    aspectRatio: '1 / 1',

                                                                                    borderRadius: '4px',
                                                                                    border: openEstimateColumnSettings
                                                                                        ? '1px solid rgba(0, 0, 0, 0.10)'
                                                                                        : 'none',

                                                                                    backgroundColor: openEstimateColumnSettings ? '#EBEBEB' : 'transparent',

                                                                                    p: 0,

                                                                                    '&:hover': {
                                                                                        backgroundColor: openEstimateColumnSettings ? '#EBEBEB' : 'transparent',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width="20"
                                                                                    height="20"
                                                                                    viewBox="0 0 20 20"
                                                                                    fill="none"
                                                                                >
                                                                                    <path
                                                                                        d="M12.4604 1.66699H7.53932V3.81826L5.96211 4.74505L4.12706 3.66859L1.6665 7.99873L3.5011 9.07491L3.50111 10.9258L1.66659 12.0019L4.12715 16.3321L5.96215 15.2557L7.53932 16.1824V18.3337H12.4604V16.1824L14.0376 15.2557L15.8725 16.3321L18.3331 12.0019L16.4987 10.9258V9.07482L18.3332 7.99873L15.8726 3.66859L14.0376 4.74502L12.4604 3.81826V1.66699Z"
                                                                                        stroke={!openEstimateColumnSettings ? '#8E8E8E' : 'black'}
                                                                                        strokeWidth="1.25"
                                                                                        strokeLinejoin="round"
                                                                                    />
                                                                                    <path
                                                                                        d="M12.9168 9.99967C12.9168 11.6105 11.611 12.9163 10.0002 12.9163C8.38933 12.9163 7.0835 11.6105 7.0835 9.99967C7.0835 8.38884 8.38933 7.08301 10.0002 7.08301C11.611 7.08301 12.9168 8.38884 12.9168 9.99967Z"
                                                                                        stroke={!openEstimateColumnSettings ? '#8E8E8E' : 'black'}
                                                                                        strokeWidth="1.25"
                                                                                    />
                                                                                </svg>
                                                                            </IconButton>
                                                                        </TableCell>

                                                                        {/* <TableCell
                                                                            align="right"
                                                                            sx={{ ...ESTIMATE_LIST_ACTION_COL, paddingRight: 1 }}
                                                                            aria-label="Actions"
                                                                        /> */}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {estimateData &&
                                                                        estimateData?.map((row) => (
                                                                            <TableRow
                                                                                key={row._id}
                                                                                hover={true}
                                                                                sx={{ cursor: 'pointer' }}
                                                                                onClick={() => {
                                                                                    if (row?.status === 'request') {
                                                                                        handleOpen2();
                                                                                        setEstimateId(row?._id);
                                                                                    } else if (row?.status === 'accept' || row?.status === 'decline') {
                                                                                        // Answered estimate requests have their own page.
                                                                                        navigate(`/estimates/${row.status}/${row?._id}`);
                                                                                    } else {
                                                                                        navigate(getEstimateDetailUrl(row));
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <TableCell component="th" scope="row" align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    {row?.estimateNumber}
                                                                                </TableCell>
                                                                                <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    {row?.estimateDate
                                                                                        ? moment(row?.estimateDate).format('MMMM D, YYYY')
                                                                                        : row?.createdAt
                                                                                          ? moment(row?.createdAt).format('MMMM D, YYYY')
                                                                                          : ''}
                                                                                </TableCell>
                                                                                <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    {row?.customer?.fullName ? (
                                                                                        row.customer.fullName
                                                                                    ) : (
                                                                                        <>
                                                                                            {row?.customer?.firstName &&
                                                                                                row?.customer?.firstName?.charAt(0)?.toUpperCase() +
                                                                                                    row?.customer?.firstName?.slice(1)}{' '}
                                                                                            {row?.customer?.lastName &&
                                                                                                row?.customer?.lastName?.charAt(0)?.toUpperCase() +
                                                                                                    row?.customer?.lastName?.slice(1)}
                                                                                        </>
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    {formatEstimateVehicleDisplay(row?.vehicle)}
                                                                                </TableCell>
                                                                                {showEstimateLicensePlateColumn && (
                                                                                    <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                        {formatEstimateLicensePlateDisplay(row?.vehicle)}
                                                                                    </TableCell>
                                                                                )}
                                                                                {showEstimateUnitColumn && (
                                                                                    <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                        {row?.vehicle?.fleetUnitNumber
                                                                                            ? String(row.vehicle.fleetUnitNumber)
                                                                                            : '—'}
                                                                                    </TableCell>
                                                                                )}

                                                                                <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    {row?.Total
                                                                                        ? NumericEngine.format(row.Total, {
                                                                                              formatCurrency: true,
                                                                                              trailingZeros: true,
                                                                                          })
                                                                                        : ''}
                                                                                </TableCell>

                                                                                <TableCell align="left" sx={ESTIMATE_LIST_BODY_CELL}>
                                                                                    <EstimateRowStatusChip row={row} />
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    align="right"
                                                                                    sx={{ ...ESTIMATE_LIST_BODY_CELL, ...ESTIMATE_LIST_ACTION_COL }}
                                                                                >
                                                                                    {row?.status && (
                                                                                        <Box display="flex" justifyContent="flex-end">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    background: '#EBEBEB',
                                                                                                    width: '24px',
                                                                                                    height: '24px',
                                                                                                }}
                                                                                                onClick={(event) => {
                                                                                                    event.stopPropagation();
                                                                                                    handleMenuOpen(event, row);
                                                                                                }}
                                                                                            >
                                                                                                <MoreVert
                                                                                                    sx={{
                                                                                                        width: '18px',
                                                                                                        height: '18px',
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'center',
                                                                                                        justifyContent: 'center',
                                                                                                    }}
                                                                                                />
                                                                                            </IconButton>
                                                                                        </Box>
                                                                                    )}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                        <EstimateRequestModal
                                                            open={open2}
                                                            handleCancel={handleClose2}
                                                            estimateData={estimateDetail}
                                                            id={estimateId}
                                                            handleClose={handleClose2}
                                                        />
                                                        <Grid container item alignItems={'center'} justifyContent="end">
                                                            <Grid item>
                                                                {showLoader ? (
                                                                    ''
                                                                ) : (
                                                                    <TablePagination
                                                                        rowsPerPageOptions={[5, 10, 25]}
                                                                        colSpan={3}
                                                                        count={estimate?.totalCount}
                                                                        rowsPerPage={rowsPerPage}
                                                                        page={page}
                                                                        slotProps={{
                                                                            select: {
                                                                                'aria-label': 'rows per page',
                                                                            },
                                                                            actions: {
                                                                                showFirstButton: true,
                                                                                showLastButton: true,
                                                                            },
                                                                        }}
                                                                        onPageChange={handleChangePage}
                                                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </CustomTabPanel>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </>
                </Grid>
            </Box>

            <ManageColumnsMenu
                anchorEl={estimateColumnAnchorEl}
                open={openEstimateColumnSettings}
                onClose={handleEstimateColumnSettingsClose}
                columns={[
                    { key: 'licensePlate', label: 'Licence Plate', checked: showEstimateLicensePlateColumn, onToggle: setShowEstimateLicensePlateColumn },
                    { key: 'unit', label: 'Unit #', checked: showEstimateUnitColumn, onToggle: setShowEstimateUnitColumn },
                ]}
            />

            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorElDropdown}
                open={openDropdown}
                onClose={handleMenuClose}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '4px!important',
                        boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 5px 5px -3px rgba(0, 0, 0, 0.20)',
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (!selectedRow) return;
                        navigate(`/estimates/edit/${selectedRow?._id}`);
                        handleMenuClose();
                        console.log(' estimateId ', selectedRow?._id);
                    }}
                >
                    <Edit style={{ marginRight: '10px' }} /> Edit Estimate
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (!selectedRow) return;
                        navigate(`/customers/${selectedRow?.customer?._id}/${selectedRow?.customer?.createdFrom}`);
                        handleMenuClose();
                    }}
                >
                    <Visibility />
                    <span style={{ marginLeft: '10px' }}>View Customer</span>
                </MenuItem>

                {selectedRow?.status !== 'draft' &&
                    (isLinkedInvoiceOutOfDate ? (
                        <MenuItem
                            onClick={async () => {
                                handleMenuClose();
                                setShowLoader(true);
                                const data = await dispatch(estimateDetailApi(selectedRow?._id));
                                setShowLoader(false);
                                navigate(`/invoice/edit-invoice-from-estimate/${linkedInvoice?._id}`, { state: { estimateData: data || selectedRow } });
                            }}
                        >
                            <SyncUpdateIcon />
                            <span style={{ marginLeft: '2px' }}>Update To Invoice</span>
                        </MenuItem>
                    ) : hasLinkedInvoice ? (
                        <MenuItem
                            onClick={() => {
                                navigate(`${getLinkedInvoiceUrl()}`);
                            }}
                        >
                            <Receipt style={{ marginRight: '10px' }} /> View Invoice
                        </MenuItem>
                    ) : canConvertToInvoice ? (
                        <MenuItem
                            onClick={async () => {
                                setShowLoader(true);
                                let data = await dispatch(estimateDetailApi(selectedRow?._id));
                                if (data) {
                                    setEstimateData(data);
                                    if (isIssuedEstimate(data?.status) && !data?.isEmailSent) {
                                        fetchCustomerDetail(data);
                                    }
                                }
                                setShowLoader(false);
                                handleMenuClose();

                                navigate(`/invoice/new-invoice-from-estimate`, { state: { estimateData: data } });
                            }}
                        >
                            {' '}
                            <div
                                style={{
                                    marginRight: '8px',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <g clip-path="url(#clip0_19802_25958)">
                                        <path
                                            d="M15 14.1667H5V12.5H15V14.1667ZM15 10.8334H5V9.16669H15V10.8334ZM15 7.50002H5V5.83335H15V7.50002ZM2.5 18.3334L3.75 17.0834L5 18.3334L6.25 17.0834L7.5 18.3334L8.75 17.0834L10 18.3334L11.25 17.0834L12.5 18.3334L13.75 17.0834L15 18.3334L16.25 17.0834L17.5 18.3334V1.66669L16.25 2.91669L15 1.66669L13.75 2.91669L12.5 1.66669L11.25 2.91669L10 1.66669L8.75 2.91669L7.5 1.66669L6.25 2.91669L5 1.66669L3.75 2.91669L2.5 1.66669V18.3334Z"
                                            fill="black"
                                        />
                                        <path
                                            d="M15.5 9.5C18.8138 9.5 21.5 12.1862 21.5 15.5C21.5 18.8138 18.8138 21.5 15.5 21.5C12.1862 21.5 9.5 18.8138 9.5 15.5C9.5 12.1862 12.1862 9.5 15.5 9.5ZM15.4502 15.4502H13.25V15.5498H15.4502V17.75H15.5498V15.5498H17.75V15.4502H15.5498V13.25H15.4502V15.4502Z"
                                            fill="white"
                                            stroke="white"
                                        />
                                        <path
                                            d="M15.5 11C13.0147 11 11 13.0147 11 15.5C11 17.9853 13.0147 20 15.5 20C17.9853 20 20 17.9853 20 15.5C20 13.0147 17.9853 11 15.5 11ZM17.75 15.95H15.95V17.75H15.05V15.95H13.25V15.05H15.05V13.25H15.95V15.05H17.75V15.95Z"
                                            fill="black"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_19802_25958">
                                            <rect width="20" height="20" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            Convert to Invoice
                        </MenuItem>
                    ) : null)}
                {selectedRow?.status !== 'draft' &&
                    // Linked work order behind the estimate → Update to Work Order (spec §6.5).
                    (estimateWorkOrderToSync(selectedRow) ? (
                        <MenuItem
                            onClick={async () => {
                                const workOrder = estimateWorkOrderToSync(selectedRow);
                                setShowLoader(true);
                                const data = await dispatch(estimateDetailApi(selectedRow?._id));
                                setShowLoader(false);
                                handleMenuClose();
                                navigate(getWorkOrderSyncFromEstimateUrl(workOrder._id), { state: { estimateData: data || selectedRow } });
                            }}
                        >
                            <SyncUpdateIcon /> Update Work Order
                        </MenuItem>
                    ) : hasLinkedWorkOrder ? (
                        <MenuItem
                            onClick={() => {
                                navigate(`${getLinkedWorkOrderUrl()}`);
                                handleMenuClose();
                            }}
                        >
                            <div
                                style={{
                                    marginRight: '8px',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CreateWorkOrderSvg />
                            </div>
                            View Work Order
                        </MenuItem>
                    ) : canCreateWorkOrder ? (
                        <MenuItem
                            onClick={async () => {
                                setShowLoader(true);
                                const data = await dispatch(estimateDetailApi(selectedRow?._id));
                                if (data) {
                                    setEstimateData(data);
                                }
                                setShowLoader(false);
                                handleMenuClose();

                                navigate(`/work-orders/new-from-estimate`, { state: { estimateData: data } });
                            }}
                        >
                            <div
                                style={{
                                    marginRight: '8px',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CreateWorkOrderSvg />
                            </div>
                            Create Work Order
                        </MenuItem>
                    ) : null)}
                {isIssuedEstimate(selectedRow?.status) && selectedRow?.isEmailSent && (
                    <MenuItem
                        onClick={() => {
                            handleSend();
                            handleMenuClose();
                        }}
                    >
                        <ResendSvg />
                        <span style={{ marginLeft: '15px' }}>Resend</span>
                    </MenuItem>
                )}
                {isIssuedEstimate(selectedRow?.status) && (
                    <MenuItem
                        onClick={() => {
                            if (!selectedRow) return;
                            navigate(`/estimates/new`, {
                                state: {
                                    duplicateEstimateId: selectedRow?._id,
                                },
                            });
                            handleMenuClose();
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: '10px' }}>
                            <path
                                d="M3.33464 18.3333H13.3346C13.7767 18.3333 14.2006 18.1577 14.5131 17.8452C14.8257 17.5326 15.0013 17.1087 15.0013 16.6667V6.66667C15.0013 6.22464 14.8257 5.80072 14.5131 5.48816C14.2006 5.17559 13.7767 5 13.3346 5H3.33464C2.89261 5 2.46868 5.17559 2.15612 5.48816C1.84356 5.80072 1.66797 6.22464 1.66797 6.66667V16.6667C1.66797 17.1087 1.84356 17.5326 2.15612 17.8452C2.46868 18.1577 2.89261 18.3333 3.33464 18.3333ZM5.0013 10.8333H7.5013V8.33333H9.16797V10.8333H11.668V12.5H9.16797V15H7.5013V12.5H5.0013V10.8333Z"
                                fill="black"
                            />
                            <path
                                d="M16.668 1.66675H6.66797V3.33341H16.668V13.3334H18.3346V3.33341C18.3346 2.41425 17.5871 1.66675 16.668 1.66675Z"
                                fill="black"
                            />
                        </svg>
                        Duplicate Estimate
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        setIsDeleteModal(true);
                        setAnchorElDropdown(null);
                    }}
                    style={{ color: '#D32F2F!important' }}
                >
                    <Delete style={{ marginRight: '10px', color: '#D32F2Fed' }} /> <span style={{ color: '#D32F2F' }}> Delete Estimate </span>
                </MenuItem>
            </Menu>

            <CommonDialog
                loader={deleteLoader}
                open={isDeleteModal}
                onClose={() => {
                    setIsDeleteModal(false);
                }}
                onSave={async () => {
                    try {
                        setDeleteLoader(true);

                        const resp = await deleteFinalizedEstimate(selectedRow?._id);
                        console.log('data', selectedRow?._id);

                        if (resp?.success) {
                            queryClient.invalidateQueries({ queryKey: estimateQueryKeys.all, refetchType: 'all' });
                        }
                    } catch (err) {
                        console.error('Delete error:', err);
                    } finally {
                        const updatedParams = { ...params, page };
                        updatedParams.page = page + 1;
                        estimateDetailsData(updatedParams);

                        setIsDeleteModal(false);
                        setDeleteLoader(false);
                        setSelectedRow(null);
                    }
                }}
                dialogTitle={'Delete Estimate?'}
                dialogSubtitle={`Are you sure you want to delete Estimate ${selectedRow?.estimateNumber || ''}? This action cannot be undone.`}
            />

            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Estimates;
